import React, { useEffect, useState } from 'react';
import { View, Pressable, TextInput, ScrollView, StyleSheet } from 'react-native';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';

export default function WorkTaskBoundaryCard({ review, busy = false, sourceActive = false, onResolve, onCancel }) {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [brief, setBrief] = useState('');
  const [prompt, setPrompt] = useState(review?.message || '');
  const [error, setError] = useState('');
  useEffect(() => {
    setExpanded(review?.decision?.reason === 'mixed_request' || (!!review?.decision?.needsReferences
      && !['ambiguous', 'classification_unavailable'].includes(review?.decision?.reason)));
    setSelected([]); setBrief(''); setPrompt(review?.message || ''); setError('');
  }, [review?.requestId]);
  if (!review) return null;
  const mixed = review.decision?.reason === 'mixed_request';
  const uncertain = ['ambiguous', 'classification_unavailable'].includes(review.decision?.reason);
  const launching = review.state === 'launching';
  const act = async (choice) => {
    setError('');
    try {
      if (choice === 'cancel') await onCancel?.();
      else await onResolve?.(choice, { referenceFileIds: selected, transferBrief: brief, effectiveMessage: prompt });
    } catch (err) { setError(err?.message || 'Could not handle this request. Please retry.'); }
  };
  const foreground = colors.text_color_primary;
  const inputStyle = [styles.input, { color: foreground, borderColor: colors.border_color, backgroundColor: colors.solid_color_primary }];
  return (
    <View testID="work-task-boundary-card" style={[styles.card, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color }]}>
      <View style={styles.heading}>
        <Icon name="arrow-up-right-from-square" size={15} color={foreground} />
        <Text weight="bold" style={{ flex: 1 }}>{launching ? 'Your new task is starting' : uncertain ? 'Where would you like to work on this?' : 'Give this project its own work chat'}</Text>
      </View>
      <Text size="s" color="secondary" style={styles.description}>
        {mixed ? 'This request includes work on the current project and a new one. Edit the new-task brief below; your original request stays here for reference.'
          : uncertain ? 'Continue this project, or start fresh with a separate chat and its own files.'
            : 'Start with a fresh plan and timeline. Bring over only the references you choose.'}
      </Text>
      <ScrollView style={{ maxHeight: expanded ? 290 : 76 }} keyboardShouldPersistTaps="handled">
        {!expanded ? <Text size="s" numberOfLines={3} style={styles.preview}>{review.message}</Text> : (
          <View style={{ gap: 10 }}>
            <Text size="s" weight="bold">New-task brief</Text>
            <TextInput accessibilityLabel="New-task brief" multiline value={prompt} onChangeText={setPrompt} editable={!busy && !launching} style={[inputStyle, { minHeight: 75 }]} />
            <Text size="s" weight="bold">References to bring over</Text>
            <Text size="xs" color="secondary">Nothing from the previous project is selected automatically. New attachments are included.</Text>
            {(review.references || []).map(file => (
              <Pressable key={file.id} accessibilityRole="checkbox" accessibilityLabel={file.name}
                aria-checked={selected.includes(file.id)} aria-disabled={busy || launching}
                accessibilityState={{ checked: selected.includes(file.id), disabled: busy || launching }} disabled={busy || launching}
                onPress={() => setSelected(ids => ids.includes(file.id) ? ids.filter(id => id !== file.id) : [...ids, file.id])}
                style={styles.reference}>
                <Icon name={selected.includes(file.id) ? 'square-check' : 'square'} size={16} color={foreground} />
                <Text size="s" style={{ flex: 1 }} numberOfLines={2}>{file.name}</Text>
              </Pressable>
            ))}
            {!review.references?.length && <Text size="xs" color="secondary">No saved reference files in this chat.</Text>}
            <TextInput accessibilityLabel="Context to bring over" placeholder="Optional: style, character details, or later corrections to keep…"
              placeholderTextColor={colors.text_color_tertiary} multiline value={brief} onChangeText={setBrief} editable={!busy && !launching}
              style={[inputStyle, { minHeight: 65 }]} />
          </View>
        )}
      </ScrollView>
      <Pressable accessibilityRole="button" disabled={busy} onPress={() => setExpanded(!expanded)} style={{ paddingVertical: 9 }}>
        <Text size="xs" color="secondary">{expanded ? 'Hide details' : 'Edit brief and choose references'}</Text>
      </Pressable>
      {sourceActive && <Text size="xs" color="secondary" style={{ marginBottom: 10 }}>
        {review.decision?.stopSource ? 'The current task will stop before the new one starts. Submitted media jobs may still finish.' : 'Your current task will keep running in its own chat.'}
      </Text>}
      {!!(error || review.error) && <Text size="s" style={{ color: colors.color_destructive, marginBottom: 10 }}>{error || review.error}</Text>}
      <View style={styles.actions}>
        {!launching && uncertain && <Pressable accessibilityRole="button" accessibilityLabel="Continue this project" disabled={busy}
          onPress={() => act('continue')} style={[styles.primary, { backgroundColor: foreground, opacity: busy ? 0.6 : 1 }]}>
          <Text size="s" weight="bold" style={{ color: colors.solid_color_primary }}>{busy ? 'Working…' : 'Continue this project'}</Text>
        </Pressable>}
        <Pressable accessibilityRole="button" accessibilityLabel={launching ? 'Recover new task' : 'Start new work chat'} disabled={busy || !prompt.trim()}
          onPress={() => act('new_task')} style={uncertain && !launching ? styles.secondary : [styles.primary, { backgroundColor: foreground, opacity: busy ? 0.6 : 1 }]}>
          <Text size="s" weight="bold" style={{ color: uncertain && !launching ? foreground : colors.solid_color_primary }}>{busy ? 'Working…' : launching ? 'Recover new task' : 'Start new chat'}</Text>
        </Pressable>
        {!launching && !uncertain && <Pressable accessibilityRole="button" accessibilityLabel="Continue this project" disabled={busy} onPress={() => act('continue')} style={styles.secondary}>
          <Text size="s">Continue this project</Text>
        </Pressable>}
        {!launching && <Pressable accessibilityRole="button" accessibilityLabel="Cancel new task request" disabled={busy} onPress={() => act('cancel')} style={styles.secondary}>
          <Text size="s" color="secondary">Cancel</Text>
        </Pressable>}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: 16, padding: 18, marginHorizontal: 8, marginBottom: 10, gap: 3 },
  heading: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  description: { marginTop: 6, marginBottom: 12, lineHeight: 20 },
  preview: { lineHeight: 20 },
  input: { borderWidth: 1, borderRadius: 8, padding: 10, fontSize: 14, textAlignVertical: 'top' },
  reference: { flexDirection: 'row', gap: 10, alignItems: 'center', paddingVertical: 5 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  primary: { borderRadius: 9, paddingHorizontal: 15, paddingVertical: 11 },
  secondary: { paddingHorizontal: 10, paddingVertical: 11 },
});
